import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './orderPullTray.css';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  cancelOrderPullTray,
  completeOrderPullTray,
  fetchOrderPullTray,
  listOpenOrderPullTrays,
  NO_ELIGIBLE_ORDERS_REASON,
  ORDER_PULL_PATH,
  orderPullTrayPath,
  recordOrderPullException,
  setOrderPullLinesPicked,
  setOrderPullSlotsVerified,
  shortPickKindLabel,
  shortPickTargets,
  startOrderPullTray,
  type OrderPullCancelResult,
  type OrderPullExceptionKind,
  type OrderPullSlot,
  type OrderPullSlotLine,
  type OrderPullTrayDetail,
  type OrderPullTraySummary,
  type OrderPullWalkLine,
} from '../api/orderPull';
import { OrderPullShortPick } from '../components/orders/OrderPullShortPick';
import { OrdersSectionNav } from '../components/orders/OrdersSectionNav';
import {
  DESKTOP_PREVIEW_HEIGHT,
  DESKTOP_PREVIEW_WIDTH,
  PHONE_PREVIEW_HEIGHT,
  PHONE_PREVIEW_WIDTH,
  cardPreviewOrigin,
  higherResCardImageUrl,
} from '../lib/cardPreviewPosition';
import { displayCondition, finishFromCopy, finishLabel, isFoilFromFinish } from '../lib/cardVariantOptions';
import { borderRadius, colors, spacing, typography, typographyFlat } from '../styles/tokens';
import { formatInstant } from '../utils/datetime';

const WORKFLOW_STEPS = [
  { id: 'trays', label: 'Open trays' },
  { id: 'walk-list', label: 'Walk list' },
] as const;

const BANNER_WORDS: Record<string, string> = {
  cancelled: 'Cancelled',
  removed: 'Removed',
  qty_up: 'Quantity up',
  qty_down: 'Quantity down',
  new_line: 'New line',
};

function bannerWord(kind: string): string {
  return BANNER_WORDS[kind] ?? kind;
}

function slotCountLabel(count: number): string {
  return count === 1 ? '1 slot' : `${count} slots`;
}

function titleCase(value: string): string {
  if (!value) {
    return value;
  }
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function languageBadge(language: string): string {
  const code = language.trim().toUpperCase();
  if (code === 'JA' || code === 'JP' || code === 'JAPANESE') {
    return 'JP';
  }
  return language.trim();
}

function lineBadges(line: OrderPullWalkLine): { key: string; label: string }[] {
  const badges: { key: string; label: string }[] = [];
  if (line.highlights.price) {
    badges.push({ key: 'price', label: '>$5' });
  }
  if (line.highlights.language && line.language) {
    badges.push({ key: 'language', label: languageBadge(line.language) });
  }
  if (line.highlights.frames) {
    line.frame_badges.forEach((frame, index) => {
      badges.push({ key: `frame-${index}-${frame}`, label: titleCase(frame) });
    });
  }
  line.banners.forEach((kind) => {
    badges.push({ key: `banner-${kind}`, label: bannerWord(kind) });
  });
  return badges;
}

/** Etched and reverse holofoil count as foil. Server foil highlights do too. */
function lineIsFoil(line: OrderPullWalkLine): boolean {
  return line.highlights.foil || isFoilFromFinish(finishFromCopy(line.is_foil, line.printing));
}

function printingText(line: OrderPullWalkLine): string {
  const stamp = (line.printing_stamp || '').trim();
  if (stamp) {
    return stamp;
  }
  return finishLabel(line.is_foil, line.printing);
}

function conditionText(condition: string | null | undefined): string {
  const value = (condition || '').trim();
  return value ? displayCondition(value) : '—';
}

function conditionIsPlayed(condition: string | null | undefined): boolean {
  const value = (condition || '').trim().toUpperCase();
  return value !== '' && value !== 'NM' && value !== 'NEAR MINT';
}

function qtyText(qty: number): string {
  return qty > 1 ? `x${qty}` : String(qty);
}

function slotToken(slotNumber: number, qty: number): string {
  return qty > 1 ? `${slotNumber}x${qty}` : String(slotNumber);
}

function slotChipLabel(slotNumber: number, qty: number): string {
  if (qty > 1) {
    return `Slot ${slotNumber}, quantity ${qty}`;
  }
  return `Slot ${slotNumber}`;
}

function collectorLabel(number: string | null | undefined): string | null {
  const value = (number || '').trim();
  if (!value) {
    return null;
  }
  return value.startsWith('#') ? value : `#${value}`;
}

/** Keep the API order. Main arrives set A-Z then card A-Z; only consecutive sets share a heading. */
function groupConsecutiveBySet(
  lines: OrderPullWalkLine[],
): { setName: string; lines: OrderPullWalkLine[] }[] {
  const groups: { setName: string; lines: OrderPullWalkLine[] }[] = [];
  for (const line of lines) {
    const setName = (line.set_name || '').trim() || '—';
    const last = groups[groups.length - 1];
    if (last && last.setName === setName) {
      last.lines.push(line);
    } else {
      groups.push({ setName, lines: [line] });
    }
  }
  return groups;
}

function lineKey(line: OrderPullWalkLine): string {
  return [
    line.product_id ?? '',
    line.card_name,
    line.set_name ?? '',
    line.collector_number ?? '',
    line.condition ?? '',
    line.printing,
    line.language,
  ].join('|');
}

function formatApiError(err: unknown, fallback: string): string {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404) {
    return 'Pull tray not found';
  }
  const detail = (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (err instanceof Error && err.message) {
    return err.message;
  }
  return fallback;
}

function BannerWords({ banners }: { banners: string[] }) {
  if (banners.length === 0) {
    return <span>—</span>;
  }
  return (
    <span>
      {banners.map((kind) => (
        <span key={kind} style={styles.word}>
          {bannerWord(kind)}
        </span>
      ))}
    </span>
  );
}

function lineIds(line: OrderPullWalkLine): string[] {
  return line.line_ids ?? [];
}

function orderNumberLabel(externalId: string | null | undefined): string | null {
  const value = (externalId || '').trim();
  if (!value) {
    return null;
  }
  return value.startsWith('#') ? value : `#${value}`;
}

function slotSummary(slot: OrderPullSlot): string {
  const parts = [`Slot ${slot.slot_number}`];
  const orderNumber = orderNumberLabel(slot.external_id);
  if (orderNumber) {
    parts.push(orderNumber);
  }
  const name = (slot.buyer_name || '').trim();
  if (name) {
    parts.push(name);
  }
  parts.push(slot.pullable ? 'Pullable' : 'Not pullable');
  return parts.join(' - ');
}

function pickProgress(tray: OrderPullTrayDetail): string {
  const lines = [...tray.main, ...tray.secure];
  const picked = lines.filter((line) => line.picked).length;
  return `Picked ${picked} of ${lines.length}`;
}

function shortPickBusyIds(savingKeys: ReadonlySet<string>): ReadonlySet<string> {
  const ids = new Set<string>();
  for (const key of savingKeys) {
    if (key.startsWith('short:')) {
      ids.add(key.slice('short:'.length));
    }
  }
  return ids;
}

function slotLineHasFlag(line: OrderPullSlotLine): boolean {
  return line.banners.length > 0 || (line.exceptions?.length ?? 0) > 0;
}

function stopRowToggle(event: React.SyntheticEvent) {
  event.stopPropagation();
}

export function WalkThumb({
  imageUrl,
  previewUrl,
}: {
  imageUrl: string | null | undefined;
  previewUrl?: string | null;
}) {
  const url = (imageUrl || '').trim();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const previewRef = useRef<HTMLImageElement>(null);
  const [open, setOpen] = useState(false);
  const [failed, setFailed] = useState(false);

  const placePreview = () => {
    const anchor = anchorRef.current;
    const preview = previewRef.current;
    if (!anchor || !preview) {
      return;
    }
    const box = anchor.getBoundingClientRect();
    const phone = window.matchMedia('(max-width: 720px)').matches;
    const width = preview.offsetWidth || (phone ? PHONE_PREVIEW_WIDTH : DESKTOP_PREVIEW_WIDTH);
    const height = preview.offsetHeight || (phone ? PHONE_PREVIEW_HEIGHT : DESKTOP_PREVIEW_HEIGHT);
    const origin = cardPreviewOrigin(box, { width, height }, {
      width: window.innerWidth,
      height: window.innerHeight,
    });
    preview.style.left = `${origin.left}px`;
    preview.style.top = `${origin.top}px`;
  };

  const showPreview = () => {
    placePreview();
    setOpen(true);
  };

  if (!url || failed) {
    return (
      <span
        className="order-pull-thumb order-pull-thumb-placeholder"
        style={{ borderRadius: borderRadius.md }}
        aria-hidden="true"
      />
    );
  }

  const preview = (
    <img
      ref={previewRef}
      className={open ? 'order-pull-thumb-preview is-open' : 'order-pull-thumb-preview'}
      src={higherResCardImageUrl(url, previewUrl)}
      alt=""
      width={DESKTOP_PREVIEW_WIDTH}
      height={DESKTOP_PREVIEW_HEIGHT}
      loading="lazy"
      decoding="async"
      style={{ borderRadius: borderRadius.md }}
    />
  );

  return (
    <span
      ref={anchorRef}
      className="order-pull-thumb"
      style={{ borderRadius: borderRadius.md }}
      tabIndex={0}
      aria-label="Card image"
      onClick={stopRowToggle}
      onMouseDown={stopRowToggle}
      onKeyDown={stopRowToggle}
      onMouseEnter={showPreview}
      onMouseLeave={() => setOpen(false)}
      onFocus={showPreview}
      onBlur={() => setOpen(false)}
    >
      <img
        src={url}
        alt=""
        width={26}
        height={36}
        loading="lazy"
        decoding="async"
        style={{ borderRadius: borderRadius.md }}
        onError={() => setFailed(true)}
      />
      {typeof document !== 'undefined' ? createPortal(preview, document.body) : preview}
    </span>
  );
}

function WalkColumnHead() {
  return (
    <div className="order-pull-colhead" role="row">
      <div role="columnheader" className="order-pull-pick">
        <span className="sr-only">Picked</span>
      </div>
      <div role="columnheader" className="order-pull-identity-head">
        <span className="order-pull-thumb-spacer" aria-hidden="true" />
        <span className="order-pull-name-pair">
          <span className="order-pull-field-set">Set</span>
          <span>Card Name</span>
        </span>
        <span className="order-pull-field-number">Card Number</span>
        <span className="order-pull-field-printing">Printing</span>
        <span className="order-pull-field-condition">Condition</span>
        <span className="order-pull-field-qty">Qty</span>
      </div>
      <div role="columnheader" className="order-pull-actions-head">Missing / Damaged</div>
      <div role="columnheader" className="order-pull-slots-head">Slot</div>
    </div>
  );
}

function WalkLineRow({
  line,
  busy,
  shortPickEnabled,
  shortPickBusy,
  onPickedChange,
  onShortPick,
}: {
  line: OrderPullWalkLine;
  busy: boolean;
  shortPickEnabled: boolean;
  shortPickBusy: ReadonlySet<string>;
  onPickedChange: (line: OrderPullWalkLine, picked: boolean) => void;
  onShortPick: (
    lineId: string,
    kind: OrderPullExceptionKind,
    qty: number,
    note: string,
  ) => void;
}) {
  const ids = lineIds(line);
  const badges = lineBadges(line);
  const canToggle = !busy && ids.length > 0;
  const number = collectorLabel(line.collector_number);
  const setName = (line.set_name || '').trim() || '—';
  const foil = lineIsFoil(line);
  const played = conditionIsPlayed(line.condition);
  return (
    <div
      role="row"
      className={foil ? 'order-pull-line order-pull-line-foil' : 'order-pull-line'}
      data-pull-line={line.card_name}
      aria-label={line.card_name}
      onClick={() => {
        if (canToggle) {
          onPickedChange(line, !line.picked);
        }
      }}
    >
      <div role="cell" className="order-pull-pick" onClick={stopRowToggle}>
        <input
          type="checkbox"
          checked={Boolean(line.picked)}
          disabled={!canToggle}
          aria-label={`Mark ${line.card_name} picked`}
          onChange={(event) => onPickedChange(line, event.target.checked)}
        />
      </div>
      <div role="cell" className="order-pull-identity-cell" data-pull-cluster="identity">
        <WalkThumb imageUrl={line.image_url} previewUrl={line.image_preview_url} />
        <span className="order-pull-name-pair">
          <span className="order-pull-set-name">{setName}</span>
          <span className="order-pull-field-name">
            <span
              className="order-pull-card-name"
              style={line.picked ? styles.done : undefined}
            >
              {line.card_name}
            </span>
            {badges.map((badge) => (
              <span key={badge.key} className="order-pull-pill">
                {badge.label}
              </span>
            ))}
          </span>
        </span>
        <span
          className="order-pull-field-number order-pull-collector"
          style={line.picked ? styles.done : undefined}
        >
          {number ?? '—'}
        </span>
        <span
          className={
            foil
              ? 'order-pull-field-printing order-pull-pill order-pull-pill-foil'
              : 'order-pull-field-printing order-pull-printing'
          }
        >
          {printingText(line)}
        </span>
        <span
          className={
            played
              ? 'order-pull-field-condition order-pull-condition order-pull-condition-played'
              : 'order-pull-field-condition order-pull-condition'
          }
        >
          {conditionText(line.condition)}
        </span>
        <span
          className={
            line.qty > 1
              ? 'order-pull-field-qty order-pull-qty order-pull-qty-multi'
              : 'order-pull-field-qty order-pull-qty'
          }
        >
          {qtyText(line.qty)}
        </span>
      </div>
      <div role="cell" className="order-pull-actions-cell" data-pull-cluster="actions">
        <OrderPullShortPick
          cardName={line.card_name}
          targets={shortPickTargets(line)}
          busyLineIds={shortPickBusy}
          enabled={shortPickEnabled}
          onRecord={onShortPick}
        />
      </div>
      <div role="cell" className="order-pull-slots-cell">
        {line.slots.map((slot) => (
          <span
            key={slot.slot_number}
            className="order-pull-slot-token"
            aria-label={slotChipLabel(slot.slot_number, slot.qty)}
          >
            {slotToken(slot.slot_number, slot.qty)}
          </span>
        ))}
      </div>
    </div>
  );
}

function unpickedLineCount(tray: OrderPullTrayDetail): number {
  return [...tray.main, ...tray.secure].filter((line) => !line.picked).length;
}

function unverifiedSlotCount(tray: OrderPullTrayDetail): number {
  return tray.slots.filter((slot) => !slot.verified).length;
}

function countPhrase(count: number, singular: string, plural: string): string {
  return count === 1 ? `1 ${singular}` : `${count} ${plural}`;
}

function finishWarning(unpicked: number, unverified: number): string {
  const lines = countPhrase(unpicked, 'line is not picked', 'lines are not picked');
  const slots = countPhrase(unverified, 'slot is not verified', 'slots are not verified');
  if (unpicked > 0 && unverified > 0) {
    return `${lines} and ${slots}.`;
  }
  if (unpicked > 0) {
    return `${lines}.`;
  }
  if (unverified > 0) {
    return `${slots}.`;
  }
  return 'All lines are picked and all slots are verified.';
}

function finishConfirm(unpicked: number, unverified: number): string | null {
  if (unpicked === 0 && unverified === 0) {
    return null;
  }
  return `${finishWarning(unpicked, unverified)} Complete the tray anyway?`;
}

function returnToQueueConfirm(count: number): string {
  if (count === 1) {
    return 'Return 1 order to the queue?';
  }
  return `Return ${count} orders to the queue?`;
}

function cancelConfirm(tray: OrderPullTrayDetail): string {
  if (tray.status !== 'pulled') {
    return returnToQueueConfirm(tray.order_count);
  }
  if (tray.marked_picked_count === 0) {
    return 'This tray has no pick record. Cancelling voids the tray only and does not change orders. Cancel this tray?';
  }
  return 'Picked orders revert to ready. Orders a channel already moved are left alone. Cancel this tray?';
}

function cancelResultNotice(result: OrderPullCancelResult): string {
  return `Reverted ${result.reverted.length}. Skipped ${result.skipped.length}.`;
}

function cancelNoticeFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object' || !('cancelNotice' in state)) {
    return null;
  }
  const notice = (state as { cancelNotice?: unknown }).cancelNotice;
  return typeof notice === 'string' && notice.trim() ? notice : null;
}

function WalkRows({
  lines,
  savingKeys,
  shortPickEnabled,
  onPickedChange,
  onShortPick,
}: {
  lines: OrderPullWalkLine[];
  savingKeys: ReadonlySet<string>;
  shortPickEnabled: boolean;
  onPickedChange: (line: OrderPullWalkLine, picked: boolean) => void;
  onShortPick: (
    lineId: string,
    kind: OrderPullExceptionKind,
    qty: number,
    note: string,
  ) => void;
}) {
  const shortBusy = shortPickBusyIds(savingKeys);
  return (
    <>
      {lines.map((line) => (
        <WalkLineRow
          key={lineKey(line)}
          line={line}
          busy={savingKeys.has(lineIds(line).join(','))}
          shortPickEnabled={shortPickEnabled}
          shortPickBusy={shortBusy}
          onPickedChange={onPickedChange}
          onShortPick={onShortPick}
        />
      ))}
    </>
  );
}

function WalkSection({
  title,
  lines,
  groupedBySet,
  savingKeys,
  shortPickEnabled,
  onPickedChange,
  onShortPick,
}: {
  title: string;
  lines: OrderPullWalkLine[];
  groupedBySet: boolean;
  savingKeys: ReadonlySet<string>;
  shortPickEnabled: boolean;
  onPickedChange: (line: OrderPullWalkLine, picked: boolean) => void;
  onShortPick: (
    lineId: string,
    kind: OrderPullExceptionKind,
    qty: number,
    note: string,
  ) => void;
}) {
  const groups = groupedBySet ? groupConsecutiveBySet(lines) : null;
  const rowProps = {
    savingKeys,
    shortPickEnabled,
    onPickedChange,
    onShortPick,
  };
  return (
    <section className="order-pull-section" aria-label={title}>
      <h2 className="order-pull-section-title">{title}</h2>
      {lines.length === 0 ? (
        <p className="order-pull-empty">No cards in {title.toLowerCase()}.</p>
      ) : (
        <div className="order-pull-table" role="table">
          <WalkColumnHead />
          {groups
            ? groups.map((group) => (
                <React.Fragment key={lineKey(group.lines[0])}>
                  <div className="order-pull-set-slot" role="row">
                    <h3 className="order-pull-set-heading">{group.setName}</h3>
                  </div>
                  <WalkRows lines={group.lines} {...rowProps} />
                </React.Fragment>
              ))
            : <WalkRows lines={lines} {...rowProps} />}
        </div>
      )}
    </section>
  );
}

function SlotList({
  slots,
  finished,
  savingSlots,
  onVerifiedChange,
}: {
  slots: OrderPullSlot[];
  finished: boolean;
  savingSlots: ReadonlySet<number>;
  onVerifiedChange: (slot: OrderPullSlot, verified: boolean) => void;
}) {
  return (
    <section className="order-pull-section" aria-label="Slots">
      <h2 className="order-pull-section-title">Slots</h2>
      {slots.length === 0 ? (
        <p className="order-pull-empty">No slots.</p>
      ) : (
        <ul className="order-pull-slots">
          {slots.map((slot) => {
            const busy = savingSlots.has(slot.slot_number);
            return (
              <li key={slot.slot_number} className="order-pull-slot">
                <input
                  type="checkbox"
                  checked={Boolean(slot.verified)}
                  disabled={finished || busy}
                  aria-label={`Verify slot ${slot.slot_number}`}
                  onClick={stopRowToggle}
                  onChange={(event) => onVerifiedChange(slot, event.target.checked)}
                />
                {slot.verified ? <span className="order-pull-pill">Verified</span> : null}
                <span>{slotSummary(slot)}</span>
                {(slot.banners ?? []).length > 0 && <BannerWords banners={slot.banners} />}
                {slot.lines.some(slotLineHasFlag) && (
                  <ul className="order-pull-slot-lines">
                    {slot.lines
                      .filter(slotLineHasFlag)
                      .map((line) => (
                        <li key={line.order_item_id}>
                          {line.card_name} <BannerWords banners={line.banners} />
                          {(line.exceptions ?? []).map((exc) => (
                            <span key={exc.id} className="order-pull-pill">
                              {shortPickKindLabel(exc.kind)} {exc.qty}
                              {exc.note ? ` — ${exc.note}` : ''}
                            </span>
                          ))}
                        </li>
                      ))}
                  </ul>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

export const OrderPullPage: React.FC = () => {
  const { trayId } = useParams<{ trayId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const cancelNotice = cancelNoticeFromState(location.state);
  const [trays, setTrays] = useState<OrderPullTraySummary[]>([]);
  const [listLoading, setListLoading] = useState(true);
  const [canStart, setCanStart] = useState(true);
  const [startBlockedReason, setStartBlockedReason] = useState<string | null>(null);
  const [isStarting, setIsStarting] = useState(false);
  const [tray, setTray] = useState<OrderPullTrayDetail | null>(null);
  const [trayLoading, setTrayLoading] = useState(Boolean(trayId));
  const [savingKeys, setSavingKeys] = useState<ReadonlySet<string>>(new Set());
  const [savingSlots, setSavingSlots] = useState<ReadonlySet<number>>(new Set());
  const [isCompleting, setIsCompleting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const node = summaryRef.current;
    const root = node?.closest('.order-pull-tray');
    if (!node || !(root instanceof HTMLElement)) {
      return;
    }
    const apply = () => {
      const height = node.offsetHeight;
      if (height > 0) {
        root.style.setProperty('--order-pull-summary-offset', `${height}px`);
      }
      const head = root.querySelector('.order-pull-colhead');
      if (head instanceof HTMLElement && head.offsetHeight > 0) {
        root.style.setProperty('--order-pull-head-offset', `${head.offsetHeight}px`);
      }
    };
    apply();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const observer = new ResizeObserver(apply);
    observer.observe(node);
    return () => observer.disconnect();
  }, [tray]);

  const loadOpenTrays = useCallback(async () => {
    try {
      setListLoading(true);
      setError(null);
      const data = await listOpenOrderPullTrays();
      setTrays(data.trays);
      setCanStart(data.can_start);
      setStartBlockedReason(data.can_start ? null : data.start_blocked_reason || NO_ELIGIBLE_ORDERS_REASON);
    } catch (err) {
      setError(formatApiError(err, 'Failed to load open trays'));
      setTrays([]);
      setCanStart(true);
      setStartBlockedReason(null);
    } finally {
      setListLoading(false);
    }
  }, []);

  const loadTray = useCallback(async (id: string) => {
    try {
      setTrayLoading(true);
      setError(null);
      const data = await fetchOrderPullTray(id);
      setTray(data);
    } catch (err) {
      setTray(null);
      setError(formatApiError(err, 'Failed to load tray'));
    } finally {
      setTrayLoading(false);
    }
  }, []);

  useEffect(() => {
    if (trayId) {
      void loadTray(trayId);
      return;
    }
    setTray(null);
    void loadOpenTrays();
  }, [trayId, loadTray, loadOpenTrays]);

  const handleStart = async () => {
    try {
      setIsStarting(true);
      setError(null);
      const started = await startOrderPullTray();
      if (!started.id || started.order_count === 0) {
        setCanStart(false);
        setStartBlockedReason(NO_ELIGIBLE_ORDERS_REASON);
        return;
      }
      navigate(orderPullTrayPath(started.id));
    } catch (err) {
      setError(formatApiError(err, 'Failed to start a tray'));
    } finally {
      setIsStarting(false);
    }
  };

  const handleShortPick = async (
    lineId: string,
    kind: OrderPullExceptionKind,
    qty: number,
    note: string,
  ) => {
    if (!trayId) {
      return;
    }
    const savingKey = `short:${lineId}`;
    setSavingKeys((current) => new Set(current).add(savingKey));
    try {
      setError(null);
      const next = await recordOrderPullException(
        trayId,
        lineId,
        kind,
        qty,
        note.trim() ? note.trim() : null,
      );
      setTray(next);
    } catch (err) {
      setError(formatApiError(err, 'Failed to record the short pick'));
    } finally {
      setSavingKeys((current) => {
        const next = new Set(current);
        next.delete(savingKey);
        return next;
      });
    }
  };

  const handlePickedChange = async (line: OrderPullWalkLine, picked: boolean) => {
    const ids = lineIds(line);
    if (!trayId || ids.length === 0) {
      return;
    }
    const savingKey = ids.join(',');
    setSavingKeys((current) => new Set(current).add(savingKey));
    try {
      setError(null);
      const next = await setOrderPullLinesPicked(trayId, ids, picked);
      setTray(next);
    } catch (err) {
      setError(formatApiError(err, 'Failed to save pick progress'));
    } finally {
      setSavingKeys((current) => {
        const next = new Set(current);
        next.delete(savingKey);
        return next;
      });
    }
  };

  const handleVerifiedChange = async (slot: OrderPullSlot, verified: boolean) => {
    if (!trayId || tray?.status === 'pulled' || tray?.status === 'void') {
      return;
    }
    setSavingSlots((current) => new Set(current).add(slot.slot_number));
    try {
      setError(null);
      const next = await setOrderPullSlotsVerified(trayId, [slot.slot_number], verified);
      setTray(next);
    } catch (err) {
      setError(formatApiError(err, 'Failed to save slot verification'));
    } finally {
      setSavingSlots((current) => {
        const next = new Set(current);
        next.delete(slot.slot_number);
        return next;
      });
    }
  };

  const handleComplete = async () => {
    if (
      !trayId
      || !tray
      || tray.status === 'pulled'
      || tray.status === 'void'
      || isCompleting
      || isCancelling
    ) {
      return;
    }
    const unpicked = unpickedLineCount(tray);
    const unverified = unverifiedSlotCount(tray);
    const confirmMessage = finishConfirm(unpicked, unverified);
    if (confirmMessage && !window.confirm(confirmMessage)) {
      return;
    }
    try {
      setIsCompleting(true);
      setError(null);
      const next = await completeOrderPullTray(trayId);
      setTray(next);
    } catch (err) {
      setError(formatApiError(err, 'Failed to complete tray'));
    } finally {
      setIsCompleting(false);
    }
  };

  const handleCancel = async () => {
    if (!trayId || !tray || tray.status === 'void' || isCancelling || isCompleting) {
      return;
    }
    if (!window.confirm(cancelConfirm(tray))) {
      return;
    }
    try {
      setIsCancelling(true);
      setError(null);
      const result = await cancelOrderPullTray(trayId);
      navigate(ORDER_PULL_PATH, { state: { cancelNotice: cancelResultNotice(result) } });
    } catch (err) {
      setError(formatApiError(err, 'Failed to cancel tray'));
    } finally {
      setIsCancelling(false);
    }
  };

  const startDisabled = isStarting || listLoading || !canStart;
  const blockedReason = !canStart && !listLoading ? startBlockedReason || NO_ELIGIBLE_ORDERS_REASON : null;

  return (
    <div style={trayId ? undefined : styles.container} className={trayId ? 'order-pull-tray' : undefined}>
      {!trayId && (
        <div style={styles.header}>
          <h1 style={styles.title}>Order pull</h1>
          <p style={styles.subtitle}>
            Start a tray of the oldest eligible orders. Other open trays stay open.
          </p>
          <OrdersSectionNav />
        </div>
      )}

      {!trayId && <section style={styles.card} aria-label="Workflow">
        <h2 style={styles.sectionTitle}>Workflow</h2>
        <ol style={styles.stepList}>
          {WORKFLOW_STEPS.map((step, index) => {
            const onTray = Boolean(trayId);
            const isCurrent = onTray ? step.id === 'walk-list' : step.id === 'trays';
            const traysLink = onTray && step.id === 'trays';
            return (
              <li
                key={step.id}
                style={{
                  ...styles.stepItem,
                  ...(isCurrent ? styles.stepCurrent : styles.stepDisabled),
                }}
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span style={styles.stepNum}>{index + 1}</span>
                {isCurrent ? (
                  <span>{step.label}</span>
                ) : traysLink ? (
                  <Link to={ORDER_PULL_PATH} style={styles.trayLink}>
                    {step.label}
                  </Link>
                ) : (
                  <button type="button" disabled style={styles.stepButton}>
                    {step.label}
                  </button>
                )}
                <span style={isCurrent ? styles.stepBadge : styles.stepBadgeMuted}>
                  {isCurrent ? 'Current' : traysLink ? 'Done' : 'Later'}
                </span>
              </li>
            );
          })}
        </ol>
      </section>}

      {error && <div style={styles.error}>{error}</div>}

      {!trayId && (
        <>
          <section style={styles.card} aria-label="Open trays">
            <h2 style={styles.sectionTitle}>Open trays</h2>
            {cancelNotice && (
              <p role="status" style={styles.hint}>
                {cancelNotice}
              </p>
            )}
            {listLoading ? (
              <p style={styles.hint}>Loading open trays...</p>
            ) : trays.length === 0 ? (
              <p style={styles.hint}>No open trays.</p>
            ) : (
              <ul style={styles.trayList}>
                {trays.map((openTray) => (
                  <li key={openTray.id} style={styles.trayItem}>
                    <Link to={orderPullTrayPath(openTray.id)} style={styles.trayLink}>
                      {formatInstant(openTray.started_at)}
                    </Link>
                    {' · '}
                    <span>{openTray.status}</span>
                    {' · '}
                    <span>{slotCountLabel(openTray.order_count)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section style={styles.card} aria-label="Start tray">
            <h2 style={styles.sectionTitle}>Start</h2>
            <button
              type="button"
              style={{
                ...styles.primaryButton,
                ...(startDisabled ? styles.primaryButtonDisabled : {}),
              }}
              disabled={startDisabled}
              aria-describedby={blockedReason ? 'order-pull-start-reason' : undefined}
              onClick={() => void handleStart()}
            >
              {isStarting ? 'Starting...' : 'Start'}
            </button>
            {blockedReason && (
              <p id="order-pull-start-reason" style={styles.blockedReason}>
                {blockedReason}
              </p>
            )}
          </section>
        </>
      )}

      {trayId && !tray && trayLoading && <p className="order-pull-empty">Loading tray...</p>}

      {tray && (
        <>
          <div
            ref={summaryRef}
            role="region"
            aria-label="Tray summary"
            className="order-pull-summary"
            style={{ position: 'sticky', top: 0 }}
          >
            <div className="order-pull-summary-text">
              <h1 className="order-pull-summary-title">Order pull</h1>
              <Link to={ORDER_PULL_PATH}>Open trays</Link>
              <span>Tray {tray.id}</span>
              <span>{slotCountLabel(tray.order_count)}</span>
              <span>{pickProgress(tray)}</span>
              <span>Started {formatInstant(tray.started_at)}</span>
              <span>{tray.status}</span>
              {tray.status === 'pulled' ? (
                <span>Tray complete. Ready orders are Picked.</span>
              ) : tray.status === 'void' ? null : (
                <span>{finishWarning(unpickedLineCount(tray), unverifiedSlotCount(tray))}</span>
              )}
              {tray.status === 'pulled' && tray.marked_picked_count === 0 ? (
                <span>No pick record. Cancel voids this tray only.</span>
              ) : null}
            </div>
            {tray.status === 'void' ? null : (
              <div className="order-pull-actions">
                {tray.status === 'pulled' ? null : (
                  <button
                    type="button"
                    className="order-pull-complete"
                    disabled={isCompleting || isCancelling}
                    onClick={() => void handleComplete()}
                  >
                    {isCompleting ? 'Completing...' : 'Complete tray'}
                  </button>
                )}
                <button
                  type="button"
                  className="order-pull-cancel"
                  disabled={isCancelling || isCompleting}
                  onClick={() => void handleCancel()}
                >
                  {isCancelling ? 'Cancelling...' : 'Cancel tray'}
                </button>
              </div>
            )}
          </div>
          <SlotList
            slots={tray.slots}
            finished={tray.status === 'pulled' || tray.status === 'void'}
            savingSlots={savingSlots}
            onVerifiedChange={(slot, verified) => void handleVerifiedChange(slot, verified)}
          />
          <WalkSection
            title="Main"
            lines={tray.main}
            groupedBySet
            savingKeys={savingKeys}
            shortPickEnabled={tray.status === 'picking' || tray.status === 'verifying'}
            onPickedChange={(line, picked) => void handlePickedChange(line, picked)}
            onShortPick={(lineId, kind, qty, note) => void handleShortPick(lineId, kind, qty, note)}
          />
          <WalkSection
            title="Secure"
            lines={tray.secure}
            groupedBySet={false}
            savingKeys={savingKeys}
            shortPickEnabled={tray.status === 'picking' || tray.status === 'verifying'}
            onPickedChange={(line, picked) => void handlePickedChange(line, picked)}
            onShortPick={(lineId, kind, qty, note) => void handleShortPick(lineId, kind, qty, note)}
          />
        </>
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: spacing.md,
    maxWidth: '100%',
    minWidth: 0,
  },
  header: {
    marginBottom: spacing.lg,
  },
  title: {
    margin: 0,
    fontSize: typography.h1.fontSize,
    fontWeight: typography.h1.fontWeight,
    color: colors.text,
  },
  subtitle: {
    marginTop: spacing.xs,
    marginBottom: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  card: {
    marginBottom: spacing.lg,
    padding: spacing.md,
    border: `1px solid ${colors.border}`,
    borderRadius: borderRadius.md,
    backgroundColor: colors.surface,
  },
  sectionTitle: {
    marginTop: 0,
    marginBottom: spacing.sm,
    fontSize: typography.h2.fontSize,
    fontWeight: typography.h2.fontWeight,
    color: colors.text,
  },
  hint: {
    margin: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
  },
  trayList: {
    margin: 0,
    paddingLeft: spacing.lg,
  },
  trayItem: {
    fontSize: typography.body.fontSize,
    color: colors.text,
    marginBottom: spacing.xs,
  },
  trayLink: {
    color: colors.primary,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  word: {
    display: 'inline-block',
    marginRight: spacing.sm,
    fontWeight: typographyFlat.fontWeight.semibold,
    color: colors.text,
  },
  done: {
    textDecoration: 'line-through',
    color: colors.text,
  },
  stepList: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm,
    fontSize: typography.body.fontSize,
    color: colors.textSecondary,
    padding: `${spacing.xs} ${spacing.sm}`,
    borderRadius: borderRadius.sm,
  },
  stepCurrent: {
    backgroundColor: colors.gray50,
    color: colors.text,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  stepDisabled: {
    color: colors.textSecondary,
  },
  stepNum: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 22,
    height: 22,
    borderRadius: borderRadius.sm,
    backgroundColor: colors.gray100,
    fontSize: typography.small.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  stepButton: {
    border: 'none',
    background: 'none',
    padding: 0,
    color: colors.textSecondary,
    fontSize: typography.body.fontSize,
    cursor: 'not-allowed',
    opacity: 0.6,
  },
  stepBadge: {
    marginLeft: 'auto',
    fontSize: typography.caption.fontSize,
    color: colors.primary,
  },
  stepBadgeMuted: {
    marginLeft: 'auto',
    fontSize: typography.caption.fontSize,
    color: colors.textSecondary,
  },
  primaryButton: {
    border: 'none',
    borderRadius: borderRadius.sm,
    backgroundColor: colors.primary,
    color: colors.white,
    padding: `${spacing.sm} ${spacing.md}`,
    cursor: 'pointer',
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  primaryButtonDisabled: {
    opacity: 0.55,
    cursor: 'not-allowed',
  },
  blockedReason: {
    margin: `${spacing.sm} 0 0`,
    color: colors.text,
    fontSize: typography.body.fontSize,
    fontWeight: typographyFlat.fontWeight.semibold,
  },
  error: {
    marginBottom: spacing.md,
    padding: spacing.md,
    borderRadius: borderRadius.sm,
    border: `1px solid ${colors.error}`,
    backgroundColor: `${colors.error}20`,
    color: colors.error,
    fontSize: typography.body.fontSize,
  },
};
